/**
 * Where Edit flies (`spec-onboarding-persistent-scene-edit-flight`, R2): the plan-view pose of the
 * floor Floor Plan will open on, in the live map's own size, square to the plan as the live viewer
 * draws it.
 *
 * Nothing here is a second rule. The floor is Floor Plan's (`planOpeningFloorIndex`), the box is the
 * one every plan map opens on (`planDrawingOutline`), the turn is the floor's saved placement as the
 * stage reads it (`effectiveViewRotation`, `usePlanStageFloors`), the floor's group is the live
 * twin's own (`viewedFloorTransform`), and the pose is `planViewPose`'s.
 *
 * The origin and the orientation are the live scene's (`MapCanvas`'s anchor and the site entity's
 * `rotationY`), so the camera lands square to the plan the person is looking at.
 */
import type { FloorPlannerLayout } from '@repo/frontend-types';
import {
  findLatestDesign,
  floorEntityBid,
  placementOf,
  planDrawingOutline,
  planOpeningFloorIndex,
} from '@repo/front-end-utils';
import { planFloorEntity } from '@repo/ui';
import { NO_PLAN_MACHINE_FOOTPRINTS, effectiveViewRotation } from '@repo/ui/planner';
import { planViewPose, type PlanCamera } from '../../../components/3D/planView/planCamera';
import { viewedFloorTransform } from '../../../components/3D/planView/planSceneLayers';

export interface EditFlightTargetInput {
  /** The twin's newest plan's layout, as read. */
  layout: FloorPlannerLayout | null | undefined;
  /** The floor remembered for this twin in this browser (`readPlanView`), if any. */
  rememberedFloorId: number | undefined;
  twinBid: string;
  /** The live scene's anchor. */
  origin: { latitude: number; longitude: number } | null | undefined;
  /** The live site's orientation in degrees, as `Site` turns it. */
  orientationDeg: number | null | undefined;
  /** The live map's size in CSS pixels. */
  mapSize: { width: number; height: number };
}

export type EditFlightTarget =
  | {
      kind: 'target';
      floorIndex: number;
      /** The BIDs the live tree may give the landing floor: Go Live's first, then the floor name's. */
      floorBids: string[];
      camera: PlanCamera & { pitch: 0 };
    }
  | { kind: 'no-plan' };

const finite = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n);

export const editFlightTarget = ({
  layout,
  rememberedFloorId,
  twinBid,
  origin,
  orientationDeg,
  mapSize,
}: EditFlightTargetInput): EditFlightTarget => {
  const floors = layout?.floors ?? [];
  if (floors.length === 0) return { kind: 'no-plan' };
  if (!origin || !finite(origin.latitude) || !finite(origin.longitude) || !finite(orientationDeg)) {
    return { kind: 'no-plan' };
  }
  if (!(mapSize.width > 0) || !(mapSize.height > 0)) return { kind: 'no-plan' };

  const floorIndex = planOpeningFloorIndex(floors, rememberedFloorId);
  const floor = floors[floorIndex];
  if (!floor) return { kind: 'no-plan' };
  const outline = planDrawingOutline(findLatestDesign(floor.designs), NO_PLAN_MACHINE_FOOTPRINTS);
  if (!outline) return { kind: 'no-plan' };

  const rotation = effectiveViewRotation(0, placementOf(floor));
  const frame = {
    origin: { latitude: origin.latitude, longitude: origin.longitude },
    orientationDeg,
    floor: viewedFloorTransform(floor, floorIndex, twinBid),
  };
  const { camera } = planViewPose(outline, rotation, mapSize, frame);
  if (!finite(camera.zoom) || !finite(camera.bearing) || !camera.center.every(finite)) {
    return { kind: 'no-plan' };
  }

  const goLive = planFloorEntity(floor, { twinBID: twinBid, position: floorIndex }).bID;
  const byName = floorEntityBid(twinBid, floor.name);
  const floorBids = [goLive, byName].filter((b): b is string => !!b);
  return { kind: 'target', floorIndex, floorBids, camera: { ...camera, pitch: 0 } };
};
